import { useState } from 'react';
import { ZprTooltip } from '@zpr-stencil';
import { BinIcon, ExpandIcon, PlusIcon, RefreshIcon } from '../../../icons';
import { AssetSource } from '../types';

export interface AssetDetailActionsProps {
  binned: boolean;
  canDelete: boolean;
  canRestore: boolean;
  havePermissionToDelete: boolean;
  activeSource: AssetSource;
  onMoveToBin: () => void;
  onAddToWorkspaceAssets: () => Promise<void> | void;
  /** Pass only when "Asset details" button should be visible (e.g. isToolMode && onEdit) */
  onEdit?: () => void;
  onRestore?: () => void;
}

export const AssetDetailActions = ({
  binned,
  canDelete,
  canRestore,
  activeSource,
  onMoveToBin,
  onAddToWorkspaceAssets,
  onEdit,
  onRestore,
  havePermissionToDelete,
}: AssetDetailActionsProps) => {
  const [isAddingToWorkspace, setIsAddingToWorkspace] = useState(false);

  const handleAddToWorkspaceAssets = async () => {
    setIsAddingToWorkspace(true);
    try {
      await onAddToWorkspaceAssets();
    } finally {
      setIsAddingToWorkspace(false);
    }
  };

  const renderMoveToBinTooltip = () => {
    return <ZprTooltip action="hover" placement="top">
      <button slot="trigger" aria-label="Move to bin"
      className="btn btn-ghost p0 " disabled><BinIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
      Move to bin</button>
      <p slot="tooltip">You do not have permission to delete assets</p>
    </ZprTooltip>
  }

  return (
    <div className="asset-detail-actions">
      {!binned && activeSource !== 'trash' && (
        <>
          {activeSource !== 'workspace-library' && (
            <button
              type="button"
              aria-label="Add to workspace assets"
              onClick={handleAddToWorkspaceAssets}
              className={`btn btn-ghost p0 ${isAddingToWorkspace ? ' processing' : ''}`}
              disabled={isAddingToWorkspace}
            >
              <PlusIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              {activeSource === 'user-library' ? 'Move to workspace' : 'Copy to workspace'}
            </button>
          )}

          {canDelete ? (
            <>
              {onEdit && (
                <button
                  className="btn btn-ghost p0 "
                  type="button"
                  aria-label="Asset details"
                  onClick={onEdit}
                >
                  <ExpandIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                  Asset details
                </button>
              )}
              {havePermissionToDelete ? (
                <button
                  type="button"
                  aria-label="Move to bin"
                  className="btn btn-ghost p0 "
                  onClick={onMoveToBin}
                >
                  <BinIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                  Move to bin
                </button>
              ) : (
                renderMoveToBinTooltip()
              )}
            </>
          ) : (
            renderMoveToBinTooltip()
          )}
        </>
      )}

      {binned && canRestore && activeSource === 'trash' && (
        <button
          className="btn btn-ghost p0 "
          type="button"
          aria-label="Restore file"
          onClick={onRestore}
        >
          <RefreshIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
          Restore file
        </button>
      )}
    </div>
  );
};
